import { createContext, useContext, ReactNode } from 'react';
import { useLoadScript } from '@react-google-maps/api';
import { usePublicConfig } from '@/hooks/usePublicConfig';
import { Loader2, AlertCircle } from 'lucide-react';

const libraries: ("places" | "geometry" | "visualization")[] = ['places', 'geometry', 'visualization'];

interface GoogleMapsContextType {
  isLoaded: boolean;
  loadError: Error | undefined;
  apiKey: string;
}

const GoogleMapsContext = createContext<GoogleMapsContextType | null>(null);

// Componente interno que só carrega o script quando a API key está disponível
const GoogleMapsLoader = ({ apiKey, children }: { apiKey: string; children: ReactNode }) => {
  // Singleton: só carrega UMA VEZ para toda a aplicação com ID único
  const { isLoaded, loadError } = useLoadScript({
    id: 'google-maps-script',
    googleMapsApiKey: apiKey,
    libraries,
    preventGoogleFontsLoading: true,
  });

  // Log de diagnóstico
  console.log('🗺️ GoogleMapsLoader status:', {
    isLoaded,
    hasLoadError: !!loadError,
    loadErrorMessage: loadError?.message,
    loadErrorName: loadError?.name,
    apiKeyLength: apiKey?.length || 0,
    libraries
  });

  return (
    <GoogleMapsContext.Provider value={{ isLoaded, loadError, apiKey }}>
      {children}
    </GoogleMapsContext.Provider>
  );
};

export const GoogleMapsProvider = ({ children }: { children: ReactNode }) => {
  const { data: config, isLoading, error } = usePublicConfig();
  const apiKey = config?.googleMapsApiKey || '';

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="text-sm text-muted-foreground">Carregando configuração do mapa...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4 bg-background p-4">
        <AlertCircle className="h-12 w-12 text-destructive" />
        <p className="text-lg font-semibold">Erro ao carregar configuração</p>
        <p className="text-sm text-muted-foreground text-center max-w-md">{error.message}</p>
      </div>
    );
  }

  // Aguardando API key
  if (!apiKey) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="text-sm text-muted-foreground">Aguardando chave da API...</span>
        </div>
      </div>
    );
  }

  // Só carrega o script quando a API key está disponível
  return <GoogleMapsLoader apiKey={apiKey}>{children}</GoogleMapsLoader>;
};

export const useGoogleMapsContext = () => {
  const context = useContext(GoogleMapsContext);
  if (!context) {
    throw new Error('useGoogleMapsContext must be used within GoogleMapsProvider');
  }
  return context;
};
